import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Bell,
  CopyPlus,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
  X,
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  Boxes,
  Circle,
  CircleUser,
  ClipboardList,
  Clock,
  Coins,
  Copy,
  CreditCard,
  Database,
  FileText,
  Fingerprint,
  Gift,
  GitFork,
  Globe,
  Info,
  Headphones,
  HeartPulse,
  History,
  KeyRound,
  Settings,
  Tag,
  Wallet,
  LayoutDashboard,
  Layers,
  ListChecks,
  MinusCircle,
  Network,
  Package,
  Receipt,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  SkipBack,
  SlidersHorizontal,
  Split,
  Store,
  Terminal,
  UserCheck,
  UserCog,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';

interface IconProps {
  readonly name: string;
  readonly size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly className?: string;
}

const SIZE_CLASS: Record<NonNullable<IconProps['size']>, string> = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

/**
 * Icon name → lucide component map (epic #835 consolidation). Nav items
 * and page code reference icons by Material Symbols LIGATURE NAMES; the
 * icon font itself is gone, so we translate those names to lucide
 * glyphs here. Unknown names render a neutral circle — never raw
 * ligature text.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  account_circle: CircleUser,
  account_tree: GitFork,
  add: Plus,
  close: X,
  delete: Trash2,
  edit: Pencil,
  library_add: CopyPlus,
  open_in_new: ExternalLink,
  alt_route: Split,
  article: FileText,
  assessment: BarChart3,
  assignment: ClipboardList,
  assignment_ind: UserCheck,
  content_copy: Copy,
  conversion_path: Workflow,
  credit_card: CreditCard,
  crisis_alert: AlertTriangle,
  currency_exchange: ArrowLeftRight,
  deployed_code: Boxes,
  dns: Server,
  do_not_disturb_on: MinusCircle,
  error: AlertCircle,
  fingerprint: Fingerprint,
  first_page: SkipBack,
  groups: Users,
  health_and_safety: HeartPulse,
  history: History,
  arrow_forward: ArrowRight,
  arrow_right: ArrowRight,
  hub: Network,
  info: Info,
  notifications: Bell,
  inventory_2: Package,
  manage_accounts: UserCog,
  manage_search: Search,
  payments: Coins,
  public: Globe,
  query_stats: Activity,
  receipt_long: Receipt,
  redeem: Gift,
  rule: ListChecks,
  rule_settings: SlidersHorizontal,
  refresh: RefreshCw,
  schedule: Clock,
  search: Search,
  shield_person: ShieldCheck,
  space_dashboard: LayoutDashboard,
  storage: Database,
  storefront: Store,
  support_agent: Headphones,
  terminal: Terminal,
  view_carousel: Layers,
  webhook: Webhook,
  // Tenant-console ligatures (console-web nav + legacy pages) that the
  // ops-console map never needed.
  account_balance_wallet: Wallet,
  analytics: Activity,
  dashboard: LayoutDashboard,
  sell: Tag,
  settings: Settings,
  vpn_key: KeyRound,
};

export function Icon({ name, size = 'md', className = '' }: IconProps): JSX.Element {
  const Glyph = ICON_MAP[name] ?? Circle;
  return (
    <Glyph aria-hidden="true" className={`${SIZE_CLASS[size]} shrink-0 ${className}`.trim()} />
  );
}
